import { ArrowDownTrayIcon, ArrowUpTrayIcon, DocumentArrowUpIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
    Alert,
    Button,
    Code,
    Divider,
    Group,
    Paper,
    rem,
    Stack,
    Text,
    Title,
    useMantineColorScheme,
} from '@mantine/core';
import { Dropzone, FileWithPath, MIME_TYPES } from '@mantine/dropzone';
import { useCallback, useState } from 'react';
import { BenchImportResult, benchImportService } from '../../services/benchImportService';
import { BenchImportResultTable } from './BenchImportResultTable';

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_MIME = [MIME_TYPES.csv, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];

export const BenchImportSection = () => {
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<BenchImportResult | null>(null);

    const handleDrop = useCallback(async (files: FileWithPath[]) => {
        const file = files[0];
        if (!file) {
            return;
        }

        const ext = file.name.split('.').pop()?.toLowerCase();
        if (ext !== 'xlsx' && ext !== 'csv') {
            setError('Only .xlsx and .csv files are accepted.');
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const importResult = await benchImportService.importFile(file);
            setResult(importResult);
        } catch (err: any) {
            setError(err?.message ?? 'Import failed. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    const handleReset = () => {
        setResult(null);
        setError(null);
    };

    const handleDownloadTemplate = () => {
        const csv = 'Email,First Name,Last Name,Availability Date\njohn.doe@example.com,,,2026-06-01\n,Jane,Smith,2026-07-15\nbob.wilson@example.com,,,\n';
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'bench-import-template.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Paper p="md" withBorder mt="md">
            <Stack>
                <Title order={3} size="h4" fw={600}>Bench Import</Title>
                <Text size="sm" c="dimmed">
                    Upload an .xlsx or .csv file to bulk-update bench availability dates.
                </Text>

                <Stack gap={6}>
                    <Text size="sm" fw={500}>Expected columns (header names are case-insensitive):</Text>
                    <Stack gap={2}>
                        <Text size="sm">
                            <Code>Email</Code> — person's email address <Text span c="dimmed" size="xs">(also accepted: <Code>e-mail</Code>, <Code>e_mail</Code>)</Text>
                        </Text>
                        <Text size="sm">
                            <Code>First Name</Code> + <Code>Last Name</Code> — used when email is not provided <Text span c="dimmed" size="xs">(also: <Code>firstname</Code>, <Code>lastname</Code>, <Code>surname</Code>)</Text>
                        </Text>
                        <Text size="sm">
                            <Code>Availability Date</Code> — date in <Code>YYYY-MM-DD</Code> format (optional) <Text span c="dimmed" size="xs">(also: <Code>date</Code>, <Code>availabilitydate</Code>)</Text>
                        </Text>
                    </Stack>
                    <Text size="xs" c="dimmed">
                        Each row must have at least Email or both First Name + Last Name. Leave Availability Date empty to clear the person from bench.
                    </Text>
                </Stack>

                <Divider />

                <Group>
                    <Button
                        variant="subtle"
                        size="xs"
                        leftSection={<ArrowDownTrayIcon style={{ width: rem(14), height: rem(14) }} />}
                        color="brand"
                        onClick={handleDownloadTemplate}
                    >
                        Download empty template (.csv)
                    </Button>
                </Group>

                {error && (
                    <Alert
                        icon={<ExclamationTriangleIcon style={{ width: rem(18) }} />}
                        color="red"
                        title="Import error"
                        onClose={() => setError(null)}
                        withCloseButton
                    >
                        {error}
                    </Alert>
                )}

                {!result ? (
                    <Dropzone
                        onDrop={handleDrop}
                        maxSize={MAX_FILE_SIZE_BYTES}
                        accept={ACCEPTED_MIME}
                        loading={loading}
                        disabled={loading}
                        style={{
                            borderColor: isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)',
                        }}
                    >
                        <Group justify="center" gap="xl" mih={80} style={{ pointerEvents: 'none' }}>
                            <Dropzone.Accept>
                                <ArrowUpTrayIcon style={{ width: rem(32), height: rem(32), color: 'var(--mantine-color-brand-5)' }} />
                            </Dropzone.Accept>
                            <Dropzone.Reject>
                                <ExclamationTriangleIcon style={{ width: rem(32), height: rem(32), color: 'var(--mantine-color-red-6)' }} />
                            </Dropzone.Reject>
                            <Dropzone.Idle>
                                <DocumentArrowUpIcon style={{ width: rem(32), height: rem(32), color: isDark ? 'var(--mantine-color-dark-1)' : 'var(--mantine-color-gray-6)' }} />
                            </Dropzone.Idle>
                            <Stack gap={4} align="center">
                                <Text size="md" fw={500} c={isDark ? 'white' : 'dark'}>
                                    Drop .xlsx or .csv file here
                                </Text>
                                <Text size="xs" c="dimmed">
                                    Max 5 MB · up to 1000 rows
                                </Text>
                            </Stack>
                        </Group>
                    </Dropzone>
                ) : (
                    <Stack>
                        <BenchImportResultTable result={result} />
                        <Group>
                            <Button variant="outline" onClick={handleReset} color="brand">
                                Import another file
                            </Button>
                        </Group>
                    </Stack>
                )}
            </Stack>
        </Paper>
    );
};
